import set from 'lodash/set';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import curry from 'lodash/curry';
import toNumber from 'lodash/toNumber';
import fp from 'lodash/fp';

const eitherZeroOrNumber = (obj, key) => toNumber(get(obj, key, 0));

export const findId = _id => fp.find({'_id': _id});

export const getId = fp.get('_id');

const removeUnasignedDeliveries = fp.filter(el => el.vehicleId !== 'none');

const eitherNumber = (obj, keyOne, keyTwo) =>
  toNumber(get(obj, keyOne, 0)) || toNumber(get(obj, keyTwo, 0))

const addClipedDiff = (keyOut, keyOne, keyTwo) => fp.map(el =>
  set(fp.cloneDeep(el), keyOut, Math.max(eitherZeroOrNumber(el, keyOne) - eitherNumber(el, keyTwo, keyOne), 0))
);
const addWaitingTime = addClipedDiff('totalWaitingTime', 'enrolledTime', 'waitingTime');
const addUnavailableTime = addClipedDiff('totalUnavailableTime', 'doneTime', 'waitingTime');
const addTravelTime = addClipedDiff('totalTravelTime', 'doneTime', 'enrolledTime');

const aggregateKey = (data, key) => reduce(data,
  (acc, el) => acc + eitherZeroOrNumber(el, key),
  0
);
const aggregateStats = data => reduce(
  ['weight', 'distance', 'value', 'slotCount', 'deliveryCount', 'finalCount', 'totalUnavailableTime', 'totalTravelTime', 'totalWaitingTime'],
  (acc, key) => set(acc, key, aggregateKey(data, key)),
  {}
);
const aggregateData = fp.mapValues(aggregateStats);

const addTotal = data => fp.isEmpty(data) ? {} : ({TOTAL: aggregateStats(data), ...data});

const getMeanAtt = (name, att) => fp.mapValues(el =>
  set(fp.cloneDeep(el), name, Math.round(eitherZeroOrNumber(el, att) / eitherZeroOrNumber(el, 'deliveryCount')))
);
const getMeanDistance = getMeanAtt('meanDistance', 'distance');
const getMeanTime = (keyOut, keyOne) => fp.mapValues(el =>
  set(fp.cloneDeep(el), keyOut, ((0.001 / (60 * 60)) * eitherZeroOrNumber(el, keyOne) / eitherZeroOrNumber(el, 'deliveryCount')).toFixed(1))
);
const getMeanUnavailableTime = getMeanTime('meanUnavailable', 'totalUnavailableTime');
const getMeanTravelTime = getMeanTime('meanTravel', 'totalTravelTime');
const getMeanWaitingTime = getMeanTime('meanWaiting', 'totalWaitingTime');
const getMeanValue = fp.mapValues(el =>
  set(el, 'meanValue', Math.round(0.01 * eitherZeroOrNumber(el, 'value') / eitherZeroOrNumber(el, 'deliveryCount')))
);

const addFinalCount = fp.map(el => set(fp.cloneDeep(el), 'finalCount', eitherZeroOrNumber(el, 'finalSlot')));
const addDeliveryCount = fp.map(el => set(fp.cloneDeep(el), 'deliveryCount', 1));
const addSlotCount = fp.map(el => set(fp.cloneDeep(el), 'slotCount', reduce(el.slotData, (acc, slot) => acc + !fp.isEmpty(slot), 0)));

const groupByVehicle = fp.groupBy('vehicleId');

const mapValuesWithKey = fp.mapValues.convert({ 'cap': false });
const addVehicleId = mapValuesWithKey((data, id) => set(data, 'vehicleId', id));

const getValuePerDistance = fp.mapValues(el =>
  set(
    fp.cloneDeep(el),
    'valuePerDistance',
    `${(eitherZeroOrNumber(el, 'value') / eitherZeroOrNumber(el, 'distance') / 100).toFixed(2)}`.replace(/\./, ',')
  )
);

const fixEmptySlots = fp.map(el => fp.isEmpty(el.slotData) ? set(el, 'slotData', [{}]) : el);

const sortByReversedValue = fp.compose(
  fp.reverse,
  fp.sortBy('value')
);

const shiftByOne = xs => ([...fp.tail(xs), fp.head(xs)]);

export const makeReportData = fp.compose(
  groupByVehicle,
  fixEmptySlots,
  removeUnasignedDeliveries,
);

const isNotStarted = fp.compose(
  fp.contains(fp.__, ['waiting', 'pending', 'event']),
  fp.get('status')
);

export const getVehiclesValue = fp.compose(
  aggregateData,
  groupByVehicle,
  addSlotCount,
  addDeliveryCount,
  fixEmptySlots,
  removeUnasignedDeliveries,
);

export const getVehicleValue = vehiclesData => vehicleId =>
  get(vehiclesData, [vehicleId, 'value'], 0);

export const addVehicleValue = vehiclesData => deliveries =>
  fp.map(
    el => set(el, 'vehicleValue', getVehicleValue(vehiclesData)(el.vehicleId)),
  )(deliveries);

export const filterNotStarted = fp.filter(isNotStarted);

export const makeAggregateData = fp.compose(
  shiftByOne,
  sortByReversedValue,
  getValuePerDistance,
  addVehicleId,
  addTotal,
  aggregateData,
  groupByVehicle,
  addSlotCount,
  addDeliveryCount,
  fixEmptySlots,
  removeUnasignedDeliveries,
);

export const addData = curry((key, data) =>
  fp.map(el => set(el, key, data[el.vehicleId] || 0))
);

const filterEvent = key => fp.filter({'event': key});
const filterPaused = filterEvent('paused');
const filterTransfer = fp.filter({'eventType': 'transfer'});
const filterUnavailable = filterEvent('unavailable');
const countById = fp.countBy('_id');

export const addPausedNumber = fp.compose(
  addData('paused'),
  countById,
  filterPaused
);
export const addUnavailableNumber = fp.compose(
  addData('unavailable'),
  countById,
  filterUnavailable
);

export const makeCountData = events => {
  const countByKey = key => ({[key]: filterEvent(key)(events).length});
  const countTransfer = ({'transfer': filterTransfer(events).length});
  return [
    {
      vehicleId: 'TOTAL',
      ...countTransfer,
      ...countByKey('updateDelivery'),
      ...countByKey('deleteDelivery'),
      ...countByKey('paused'),
      ...countByKey('unavailable'),
      ...countByKey('choose'),
      ...countByKey('updateMeta'),
      ...countByKey('deleteMeta'),
    }
  ];
};

export const makeAnalyticData = (deliveries, events) => fp.compose(
  shiftByOne,
  addUnavailableNumber(events),
  addPausedNumber(events),
  sortByReversedValue,
  getValuePerDistance,
  addVehicleId,
  getMeanValue,
  getMeanDistance,
  getMeanWaitingTime,
  getMeanTravelTime,
  getMeanUnavailableTime,
  addTotal,
  aggregateData,
  groupByVehicle,
  addFinalCount,
  addDeliveryCount,
  addWaitingTime,
  addTravelTime,
  addUnavailableTime,
  fixEmptySlots,
  removeUnasignedDeliveries,
)(deliveries);
